import { ReactNode } from 'react';

interface PhoneMockupProps {
  children?: ReactNode;
  className?: string;
}

/**
 * Phone mockup frame for displaying mobile app screenshots
 * TODO: Replace placeholder content with real mobile app screenshots
 */
export function PhoneMockup({ children, className = '' }: PhoneMockupProps) {
  return (
    <div className={`relative ${className}`}>
      {/* Phone frame */}
      <div className="relative mx-auto w-[280px] md:w-[320px]">
        {/* Phone bezel */}
        <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
          {/* Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-3xl z-10" />

          {/* Screen */}
          <div className="relative bg-white rounded-[2.25rem] overflow-hidden aspect-[9/19.5]">
            {children || <PlaceholderMobileUI />}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Placeholder mobile UI that mimics a field service app
 */
function PlaceholderMobileUI() {
  return (
    <div className="h-full bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Status bar placeholder */}
      <div className="h-11 bg-ink flex items-center justify-between px-6 pt-2">
        <span className="text-white text-xs font-medium">9:41</span>
        <div className="flex gap-1">
          <div className="w-4 h-2 bg-white/80 rounded-sm" />
          <div className="w-4 h-2 bg-white/80 rounded-sm" />
          <div className="w-6 h-3 bg-white rounded-sm" />
        </div>
      </div>

      {/* Header */}
      <div className="bg-ink px-4 pb-4 pt-2">
        <p className="text-white/60 text-[10px]">Good morning, Mike</p>
        <h2 className="text-white font-semibold text-sm">Today's Schedule</h2>
      </div>

      {/* Stats row */}
      <div className="flex gap-2 px-3 -mt-3">
        <StatCard value="5" label="Jobs" color="bg-accent" />
        <StatCard value="$2.4k" label="Revenue" color="bg-success" />
        <StatCard value="2" label="Pending" color="bg-warning" />
      </div>

      {/* Job cards */}
      <div className="flex-1 px-3 py-3 space-y-2 overflow-hidden">
        <JobCard
          time="8:00 AM"
          title="HVAC Maintenance"
          address="123 Oak Street"
          status="In Progress"
          statusColor="bg-accent"
        />
        <JobCard
          time="10:30 AM"
          title="AC Installation"
          address="456 Maple Ave"
          status="Upcoming"
          statusColor="bg-gray-400"
        />
        <JobCard
          time="1:00 PM"
          title="Furnace Repair"
          address="789 Pine Road"
          status="Upcoming"
          statusColor="bg-gray-400"
        />
      </div>

      {/* Bottom nav */}
      <div className="bg-white border-t border-gray-200 px-4 py-2 flex justify-around">
        <NavIcon active label="Home" />
        <NavIcon label="Jobs" />
        <NavIcon label="Time" />
        <NavIcon label="More" />
      </div>
    </div>
  );
}

function StatCard({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <div className="flex-1 bg-white rounded-lg p-2 shadow-sm">
      <div className={`w-6 h-6 ${color} rounded-full flex items-center justify-center mb-1`}>
        <span className="text-white text-[8px] font-bold">{value.charAt(0)}</span>
      </div>
      <p className="font-bold text-ink text-sm">{value}</p>
      <p className="text-gray-500 text-[8px]">{label}</p>
    </div>
  );
}

function JobCard({
  time,
  title,
  address,
  status,
  statusColor
}: {
  time: string;
  title: string;
  address: string;
  status: string;
  statusColor: string;
}) {
  return (
    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
      <div className="flex justify-between items-start mb-1">
        <span className="text-[10px] text-gray-500">{time}</span>
        <span className={`text-[8px] px-2 py-0.5 rounded-full text-white ${statusColor}`}>
          {status}
        </span>
      </div>
      <h3 className="font-semibold text-ink text-xs">{title}</h3>
      <p className="text-gray-500 text-[9px] flex items-center gap-1">
        <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {address}
      </p>
    </div>
  );
}

function NavIcon({ label, active }: { label: string; active?: boolean }) {
  return (
    <div className="flex flex-col items-center">
      <div className={`w-5 h-5 rounded ${active ? 'bg-accent' : 'bg-gray-300'}`} />
      <span className={`text-[8px] mt-0.5 ${active ? 'text-accent font-medium' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
}
